import React from 'react';
import { Tooltip, TooltipProps, styled } from '@mui/material';
import { Info } from '@mui/icons-material';

/**
 * AccessibleTooltip - A tooltip component that works with hover, keyboard focus, and screen readers
 * 
 * Features:
 * - Shows on hover
 * - Shows on keyboard focus (Tab navigation)
 * - Accessible to screen readers via aria-label
 * - Optional info icon indicator
 */

interface AccessibleTooltipProps extends Omit<TooltipProps, 'children'> {
  children: React.ReactElement;
  showIcon?: boolean;
  iconSize?: 'small' | 'inherit' | 'medium' | 'large';
}

const StyledInfoIcon = styled(Info)(({ theme }) => ({
  marginLeft: theme.spacing(0.5),
  opacity: 0.6,
  verticalAlign: 'middle',
  cursor: 'help',
  '&:hover': {
    opacity: 1,
  },
  '&:focus': {
    opacity: 1,
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

/**
 * Wraps content with an accessible tooltip
 */
export const AccessibleTooltip: React.FC<AccessibleTooltipProps> = ({
  children,
  title,
  showIcon = false,
  iconSize = 'small',
  ...props
}) => {
  // Ensure the tooltip title is accessible as aria-label
  const ariaLabel = typeof title === 'string' ? title : undefined;

  if (showIcon) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {children}
        <Tooltip
          title={title}
          arrow
          enterTouchDelay={0}
          leaveTouchDelay={3000}
          {...props}
        >
          <StyledInfoIcon 
            fontSize={iconSize}
            tabIndex={0}
            role="img"
            aria-label={ariaLabel}
          />
        </Tooltip>
      </span>
    );
  }

  return (
    <Tooltip
      title={title}
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      {...props}
    >
      {React.cloneElement(children, {
        tabIndex: children.props.tabIndex ?? 0,
        'aria-label': ariaLabel,
      })}
    </Tooltip>
  );
};

/**
 * AnalysisGroupHeader - A header for analysis groups with an accessible tooltip
 */
interface AnalysisGroupHeaderProps {
  label: string;
  tooltip: string;
  variant?: 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2';
  sx?: React.CSSProperties | object;
}

export const AnalysisGroupHeader: React.FC<AnalysisGroupHeaderProps> = ({
  label,
  tooltip,
  variant = 'subtitle1',
  sx,
}) => {
  return (
    <AccessibleTooltip title={tooltip} showIcon>
      <span 
        style={{ 
          fontWeight: 600,
          cursor: 'help',
          ...sx 
        }}
        tabIndex={0}
        role="heading"
        aria-label={`${label}. ${tooltip}`}
      >
        {label}
      </span>
    </AccessibleTooltip>
  );
};

export default AccessibleTooltip;



